import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import { TrebleClefIcon } from "@/components/TrebleClefLogo";

const Login = () => {
  const navigate = useNavigate();
  const { login, user } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorDetail, setErrorDetail] = useState("");

  // Redirect if already logged in
  React.useEffect(() => {
    if (user) {
      navigate("/dashboard");
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorDetail("");
    
    // Trim and normalize email and password (mobile keyboards often add trailing spaces)
    const trimmedEmail = email.trim().toLowerCase();
    const trimmedPassword = password.trim();
    
    if (!trimmedEmail || !trimmedPassword) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      // Clear any old tokens first
      localStorage.removeItem("token");
      
      await login(trimmedEmail, trimmedPassword);
      toast.success("Welcome back!");
      navigate("/dashboard");
    } catch (error) {
      console.error("Login error:", error);
      
      // Extract detailed error information
      let errorMessage = "Login failed";
      let statusCode = error.response?.status;
      let serverDetail = error.response?.data?.detail;
      
      if (statusCode === 401) {
        errorMessage = "Invalid email or password";
      } else if (statusCode === 404) {
        errorMessage = "Account not found";
      } else if (statusCode === 500) {
        errorMessage = "Server error - please try again";
      } else if (error.code === "ERR_NETWORK") {
        errorMessage = "Network error - check your connection";
      } else if (serverDetail) {
        errorMessage = serverDetail;
      }
      
      setErrorDetail(`${errorMessage}${statusCode ? ` (${statusCode})` : ''}`);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-obsidian flex items-center justify-center px-4 pt-20">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3">
            <TrebleClefIcon size={48} />
            <span className="font-display text-2xl">
              <span className="text-platinum">Brown</span>
              <span className="text-electric inline-block mx-0.5" style={{ transform: 'scaleX(-1.3)' }}>𝄞</span>
              <span className="text-platinum">tone</span>
            </span>
          </Link>
          <p className="text-white/50 mt-2">Welcome back to the club</p>
        </div>

        {/* Login Form */}
        <div className="bg-obsidian-light border border-white/10 rounded-lg p-8" data-testid="login-form">
          <h1 className="font-display text-2xl text-white mb-6 text-center">Sign In</h1>
          
          {/* Error Display */}
          {errorDetail && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm" data-testid="login-error">
              {errorDetail}
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-white/70">Email</Label>
              <Input 
                id="email"
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setErrorDetail(""); }}
                placeholder="your@email.com"
                className="bg-obsidian border-white/20 text-white placeholder:text-white/40 focus:border-electric"
                data-testid="login-email"
                autoComplete="email"
                autoCapitalize="none"
                autoCorrect="off"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-white/70">Password</Label>
              <div className="relative">
                <Input 
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setErrorDetail(""); }}
                  placeholder="••••••••"
                  className="bg-obsidian border-white/20 text-white placeholder:text-white/40 focus:border-electric pr-10"
                  data-testid="login-password"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button 
              type="submit"
              disabled={loading}
              className="w-full bg-electric text-obsidian hover:bg-electric-light py-6 font-medium"
              data-testid="login-submit"
            >
              {loading ? (
                <span className="w-5 h-5 border-2 border-obsidian border-t-transparent rounded-full animate-spin"></span>
              ) : (
                "Sign In"
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-white/50 text-sm">
              Don't have an account?{" "}
              <Link to="/register" className="text-electric hover:underline">
                Join the Club
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
